import { classroomsApi } from "@api/classrooms"
import { materialAssignmentsApi, materialsApi, studentMaterialsApi } from "@api/materials"
import { useToast } from "@components/ui/Toaster"
import { adaptiveLevelKeys } from "@features/adaptive-levels"
import { classroomKeys } from "@features/classrooms"
import { gradingKeys, progressKeys } from "@features/grading"
import { homeKeys } from "@features/home"
import { studentProfileKeys } from "@features/student-profile"
import { studentKeys } from "@features/students"
import { analytics } from "@lib/analytics"
import { logger } from "@lib/logger"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { materialKeys, studentMaterialsKeys } from "./queryKeys"

import { truncateTopic } from "@/constants/analytics"
import { apiErrorCode, apiErrorKeys } from "@/lib/apiError"
import { taskRunKeys, useRealtimeEvent, useTaskProgress } from "@/realtime"
import type {
  AssignmentContentResponse,
  BulkReopenOut,
  ContentEditItem,
  Material,
  MaterialAssignment,
  MaterialCut,
  RegenerationReason,
  MaterialType,
  MaterialTypeContract,
  MaterialTypesResponse,
  ReopenOut,
} from "@/types"

// Returns the refetches so a mutation can keep `isPending` until fresh data
// lands (PRD-66: a notice whose CTA stays tappable on stale data invites a
// second submit). Callers that do not need that simply ignore the promise.
const invalidateMaterialCaches = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: materialKeys.all }),
    qc.invalidateQueries({ queryKey: classroomKeys.all }),
    // PRD-10 §FR-32: material lifecycle changes affect home pendientes counts
    // and the recent-materials stream.
    qc.invalidateQueries({ queryKey: homeKeys.all }),
  ])

// `MaterialItemCountLimits` and `useMaterialItemCountLimits` lived here to
// feed the request form's numeric item-count input with a min/max range. PRD-59
// replaced that input with chips built from the contract's own
// `allowed_item_counts`, so the derived range had no consumer left; the full
// per-type contract (`useMaterialTypeContracts`, below) carries it.

// Module-level for the same referential-stability reason as
// `selectItemCountLimits` above. `code` is `string` on the wire; the
// cast narrows to the mirrored union so consumers can index by
// MaterialType (an unknown future code simply lands on no key).
const selectTypesByCode = (
  manifest: MaterialTypesResponse,
): Partial<Record<MaterialType, MaterialTypeContract>> =>
  Object.fromEntries(manifest.types.map((t) => [t.code, t])) as Partial<
    Record<MaterialType, MaterialTypeContract>
  >

/**
 * PRD-49 — the full per-type contracts, keyed by code. Shares the one
 * cached /material-types/ query (same
 * key + fn; only `select` differs). RequestMaterialModal reads the
 * `codigo` entry for the exercise-type choices and the language selector;
 * a manifest without the entry (server rollout flag off) simply never
 * offers the type.
 */
export const useMaterialTypeContracts = () =>
  useQuery({
    queryKey: materialKeys.types(),
    queryFn: materialsApi.types,
    staleTime: 60 * 60_000,
    select: selectTypesByCode,
  })

/**
 * Every material in the active classroom scope, in one cache entry.
 *
 * PRD-05 §FR-6 (2026-08-05) — the filter panel narrows this in memory, so
 * typing in the search box costs zero requests and the status pills can show
 * real cross-faceted counts. `placeholderData` keeps the previous scope's rows
 * on screen while a classroom switch settles (frontend-performance.md §9).
 */
export const useAllMaterials = (classroomId: string | null) =>
  useQuery({
    queryKey: materialKeys.scope(classroomId),
    queryFn: () => materialsApi.listAll(classroomId),
    placeholderData: (prev) => prev,
  })

/**
 * PRD-62 — how many archived materials the scope holds. Fetched with the page
 * so the collapsed section can say "Archivados · 3" or not render at all.
 */
export const useArchivedMaterialsCount = (classroomId: string | null) =>
  useQuery({
    queryKey: materialKeys.archivedCount(classroomId),
    queryFn: () => materialsApi.countArchived(classroomId),
    placeholderData: (prev) => prev,
  })

/** PRD-62 — the archived materials themselves, fetched only once `enabled`. */
export const useArchivedMaterials = (classroomId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: materialKeys.archived(classroomId),
    queryFn: () => materialsApi.listAll(classroomId, { archived: true }),
    enabled,
    placeholderData: (prev) => prev,
  })

export const useMaterial = (id: string | undefined) =>
  useQuery({
    queryKey: id ? materialKeys.detail(id) : materialKeys.details(),
    queryFn: () => materialsApi.get(id as string),
    enabled: !!id,
  })

export const useGenerateMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.generate,
    // `..._requested`, not `material_created`: the backend has only ACCEPTED
    // the job. The engine still has to produce one assignment per student and
    // can fail (PRD-18 §FR-3.6). The outcome is `material_generation_settled`,
    // emitted by the SERVER at the settlement chokepoint — a browser could
    // only report it while a tab happened to be open.
    onSuccess: (result, payload) => {
      analytics.track("material_generation_requested", {
        material_id: result.material_id,
        classroom_id: payload.classroom_id,
        material_type: payload.material_type,
        item_type: payload.item_type,
        question_count: payload.question_count,
        language: payload.language ?? null,
        education_level: result.education_level,
        total_assignments: result.total_assignments,
        // The one free-text property allowed out (PRD-57 FR-6): curricular
        // content, truncated. `payload.notes` is NOT sent and never will be —
        // that is where professors write about specific students.
        topic: truncateTopic(payload.topic),
        reference_count: payload.references?.length ?? 0,
        reference_example_count:
          payload.references?.filter((ref) => ref.intent === "ejemplo").length ?? 0,
      })
      invalidateMaterialCaches(qc)
    },
  })
}

export const useApproveMaterial = () => {
  const qc = useQueryClient()
  // PRD-42 §FR-2 — the approval carries the send decision:
  //   sendDate null / today → deliver now
  //   sendDate in the future → park in `por_enviar` for the morning sweep
  // ``studentUploadDeadline`` is an ISO date ("YYYY-MM-DD") anchored to
  // that same send date; null lets the backend default to send_date + 1d.
  return useMutation({
    mutationFn: ({
      id,
      sendDate,
      studentUploadDeadline,
    }: {
      id: string
      sendDate?: string | null
      studentUploadDeadline?: string | null
    }) => materialsApi.approve(id, { sendDate, studentUploadDeadline }),
    onSuccess: (_result, { id, sendDate }) => {
      // PRD-42: `scheduled` is the one property that says whether the
      // schedule-the-send feature is being used at all.
      analytics.track("material_approved", { material_id: id, scheduled: sendDate != null })
      invalidateMaterialCaches(qc)
    },
  })
}

// ── PRD-42 §FR-6 — managing a pending send ──────────────────────────
// Three verbs over a `por_enviar` material. Each invalidates through
// the key factory (never a string literal) because a send flips the
// material AND the home counters AND the classroom aggregates.

/** Deliver a scheduled material now, ahead of its chosen send day. */
export const useSendMaterialNow = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.sendNow,
    onSuccess: (_result, id) => {
      analytics.track("material_send_changed", { material_id: id, action: "sent_now" })
      invalidateMaterialCaches(qc)
    },
  })
}

/** Move a scheduled material to a different send day. */
export const useRescheduleMaterialSend = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, sendDate }: { id: string; sendDate: string }) =>
      materialsApi.reschedule(id, sendDate),
    onSuccess: (_result, { id }) => {
      analytics.track("material_send_changed", { material_id: id, action: "rescheduled" })
      invalidateMaterialCaches(qc)
    },
  })
}

/** Drop the pending send; the material goes back to `revision`. */
export const useCancelMaterialSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.cancelSchedule,
    onSuccess: (_result, id) => {
      analytics.track("material_send_changed", { material_id: id, action: "schedule_cancelled" })
      invalidateMaterialCaches(qc)
    },
  })
}

export const useCancelMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.cancel,
    onSuccess: (_result, id) => {
      analytics.track("material_cancelled", { material_id: id })
      invalidateMaterialCaches(qc)
    },
  })
}

export const useDeleteMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.delete,
    onSuccess: (_result, id) => {
      analytics.track("material_deleted", { material_id: id })
      invalidateMaterialCaches(qc)
    },
  })
}

// ── PRD-62 — archive / restore ───────────────────────────────────────

/**
 * Archiving moves numbers on every surface that reads grades, so it
 * invalidates more than the material caches: the roster rows and the student
 * history (averages), the progress sheet, and the adaptive levels (the server
 * re-evaluates them after the commit, before it answers).
 */
const invalidateArchiveCaches = (qc: ReturnType<typeof useQueryClient>, updated: Material) => {
  // The detail page stays mounted and must say what just happened now, not
  // after the refetch. Merge the one field instead of replacing the entry:
  // this response carries no `review_summary` (only GET detail does), and
  // writing it in would blank the results block until the refetch lands.
  qc.setQueryData<Material>(materialKeys.detail(updated.id), (prev) =>
    prev ? { ...prev, archived_at: updated.archived_at } : prev,
  )
  // Lanes + the archived section + the detail; classroom results; home.
  invalidateMaterialCaches(qc)
  void qc.invalidateQueries({ queryKey: studentKeys.all })
  void qc.invalidateQueries({ queryKey: studentProfileKeys.all })
  void qc.invalidateQueries({ queryKey: progressKeys.all })
  void qc.invalidateQueries({ queryKey: adaptiveLevelKeys.all })
}

export const useArchiveMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.archive,
    onSuccess: (updated) => {
      analytics.track("material_archived", {
        material_id: updated.id,
        classroom_id: updated.classroom_id,
        material_origin: updated.origin,
      })
      invalidateArchiveCaches(qc, updated)
    },
  })
}

export const useRestoreMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.restore,
    onSuccess: (updated) => {
      analytics.track("material_restored", {
        material_id: updated.id,
        classroom_id: updated.classroom_id,
        material_origin: updated.origin,
      })
      invalidateArchiveCaches(qc, updated)
    },
  })
}

// ── PRD-11: real-time progress + per-student grid hooks ─────────────

// The websocket is the primary path: `material.assignment_ready` fires per
// student and `material.ready` on the aggregate settle, and both invalidate
// this exact query (MaterialDetailPage). Polling is the SAFETY NET for the one
// case the socket cannot cover — connected, but an event was lost — so it runs
// at a last-resort cadence rather than competing with the push.
//
// It was 5s, which meant a 30-student generation issued ~120 needless requests
// while the push had already delivered every transition (audit F-WS-4). The
// reconnect gap, which used to be what the 5s tick really covered, is now
// closed properly by the `realtime.connected` reconcile below.
const POLL_INTERVAL_MS = Number(import.meta.env.VITE_MATERIAL_PROGRESS_POLL_SECONDS ?? 30) * 1000

// `programado` is deliberately absent (PRD-42 / ADR-0036). It used to
// mean "waiting for the generation cron", and that cron is gone — so a
// surviving historical row has nothing coming to change it, and polling
// it is an interval that can never reach a terminal state. The
// `dispatch_stranded_scheduled` command moves those rows to
// `procesando`, which is in flight for real and resumes polling here.
const isInFlight = (status: string | undefined): boolean => status === "procesando"

/**
 * Real-time progress for the per-student strip on the Material detail
 * page (PRD-11 §FR-9).
 *
 *  - Polls every POLL_INTERVAL_MS while Material.status is in flight.
 *  - Auto-stops on terminal state.
 *  - TanStack Query already pauses polling for hidden tabs via its
 *    `refetchOnWindowFocus`/`refetchInterval` semantics.
 */
export const useMaterialProgress = (id: string | undefined, status: string | undefined) => {
  const query = useQuery({
    queryKey: id ? materialKeys.progress(id) : materialKeys.progress("none"),
    queryFn: () => materialsApi.progress(id as string),
    enabled: !!id,
    refetchInterval: isInFlight(status) ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  })

  // The socket may open AFTER generation started (cold tab, reconnect after a
  // drop), and every event emitted during that window is gone — the transport
  // has no replay. Reading the row on (re)connect closes that gap immediately
  // instead of leaving it to the next poll tick. Same pattern as
  // `useTaskProgress`, which documents the reasoning.
  useRealtimeEvent("realtime.connected", () => {
    if (id && isInFlight(status)) void query.refetch()
  })

  return query
}

/** All MaterialAssignment rows for one Material — feeds the per-student grid. */
export const useMaterialAssignments = (materialId: string | undefined) =>
  useQuery({
    queryKey: materialId
      ? materialKeys.assignmentsForMaterial(materialId)
      : materialKeys.assignmentsForMaterial("none"),
    queryFn: () => materialAssignmentsApi.forMaterial(materialId as string),
    enabled: !!materialId,
    placeholderData: (prev) => prev,
  })

/** Per-student "Materiales personalizados" section (PRD-11 §FR-7). */
export const useStudentMaterials = (
  studentId: string | undefined,
  page: number = 1,
  pageSize: number = 25,
) =>
  useQuery({
    queryKey: studentId
      ? studentMaterialsKeys.list(studentId, page, pageSize)
      : studentMaterialsKeys.list("none", page, pageSize),
    queryFn: () => studentMaterialsApi.list(studentId as string, page, pageSize),
    enabled: !!studentId,
    placeholderData: (prev) => prev,
  })

export const useRetryAssignment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialAssignmentsApi.retry,
    onSuccess: () => {
      // The argument is an ASSIGNMENT id — one student — so it is not sent.
      analytics.track("material_generation_retried", { scope: "one" })
      invalidateMaterialCaches(qc)
      qc.invalidateQueries({ queryKey: studentMaterialsKeys.all })
    },
  })
}

export const useRetryFailed = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.retryFailed,
    onSuccess: (_result, id) => {
      analytics.track("material_generation_retried", { scope: "all_failed", material_id: id })
      invalidateMaterialCaches(qc)
    },
  })
}

// ── PRD-66 — students who joined the group after a material ──────────

/**
 * Generate ONE material for the students of the group who have no copy.
 * The copy follows the material: it joins the review, the scheduled send, or
 * — on a sent material — is emailed as soon as it is ready (the server owns
 * that; see `api/materials.py::backfill_missing_students`).
 */
export const useBackfillMissing = (material: Pick<Material, "id" | "classroom_id">) => {
  const qc = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation("materials")
  return useMutation({
    mutationFn: () => materialsApi.backfillMissing(material.id),
    onSuccess: (result) => {
      if (result.retried === 0) {
        // Another tab, or the roster, already did it: the page would change
        // without explaining why, so say it.
        toast.info(t("late.toast.nothing_missing"))
      } else {
        // No success toast: the notice turns into its own "Generando…"
        // status and the rows move — one surface per event
        // (notifications-toast.md §9).
        analytics.track("late_students_backfill_requested", {
          classroom_id: material.classroom_id,
          source: "material_detail",
          materials: 1,
          copies: result.retried,
        })
      }
      // `materialKeys.all` covers the detail and its per-student rows; the
      // same helper refreshes `classroomKeys.all`, which carries the roster
      // notice. Returned so the CTA stays pending until fresh data lands.
      return invalidateMaterialCaches(qc)
    },
    onError: (err) => {
      toast.error(t(...apiErrorKeys(err)))
      logger.error("materials.backfill_failed", {
        code: apiErrorCode(err),
        material_id: material.id,
      })
    },
  })
}

/** The group's materials some enrolled students have no copy of (roster notice). */
export const useClassroomMaterialGaps = (classroomId: string | undefined) => {
  // No `placeholderData`: keeping the previous classroom's gaps on screen
  // under a new classroom's key would name another group's students.
  const query = useQuery({
    queryKey: classroomKeys.materialGaps(classroomId ?? ""),
    queryFn: () => classroomsApi.materialGaps(classroomId as string),
    enabled: !!classroomId,
  })
  const { isError, error } = query
  useEffect(() => {
    // The notice simply does not appear on a failed fetch; the log is what
    // keeps that from being silent.
    if (isError) {
      logger.warning("materials.gaps_failed", {
        classroom_id: classroomId,
        code: apiErrorCode(error),
      })
    }
  }, [classroomId, error, isError])
  return query
}

/**
 * Generate the chosen materials for every student missing them, from the
 * roster. One result per material: a refusal of one does not undo the rest.
 * The notice reports the outcome in place (refused rows keep their reason),
 * so only a failed request toasts.
 */
export const useFillMaterialGaps = (classroomId: string) => {
  const qc = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation("materials")
  return useMutation({
    mutationFn: (materialIds: string[]) => classroomsApi.fillMaterialGaps(classroomId, materialIds),
    onSuccess: (results) => {
      const copies = results.reduce((sum, row) => sum + row.created, 0)
      const refused = results.filter((row) => row.error_code)
      if (copies > 0) {
        analytics.track("late_students_backfill_requested", {
          classroom_id: classroomId,
          source: "roster_banner",
          materials: results.length - refused.length,
          copies,
        })
      }
      if (refused.length > 0) {
        logger.warning("materials.fill_gaps_partial", {
          classroom_id: classroomId,
          refused: refused.length,
          codes: Array.from(new Set(refused.map((row) => row.error_code))).join(","),
        })
      }
      return invalidateMaterialCaches(qc)
    },
    onError: (err) => {
      toast.error(t(...apiErrorKeys(err)))
      logger.error("materials.fill_gaps_failed", {
        code: apiErrorCode(err),
        classroom_id: classroomId,
      })
    },
  })
}

// PRD-63 — a new version of the same material, with the professor's feedback.
// The comment is sent to the server and nowhere else: analytics gets whether
// there was one and its length.
export const useRegenerateMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      material,
      reasons,
      comment,
    }: {
      material: Material
      reasons: RegenerationReason[]
      comment: string
    }) => materialsApi.regenerate({ id: material.id, reasons, comment }),
    onSuccess: (result, { material, reasons, comment }) => {
      analytics.track("material_regeneration_requested", {
        material_id: material.id,
        classroom_id: material.classroom_id,
        round: result.round,
        reasons,
        has_comment: comment.trim().length > 0,
        comment_length: comment.trim().length,
        prior_status: material.status,
        students: result.total_assignments,
      })
      invalidateMaterialCaches(qc)
    },
  })
}

// PRD-13 follow-up — bulk re-send of already-delivered emails.
// Distinct intent from useRetryFailed: that one targets failed/bounced;
// this targets the dominant ``sent`` case (student deleted the email,
// spam filter, etc.). Audited as ``material.email.resend_all``.
export const useResendAllEmails = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialsApi.resendAllEmails,
    onSuccess: (_result, id) => {
      analytics.track("material_email_resent", { scope: "all", material_id: id })
      invalidateMaterialCaches(qc)
    },
  })
}

// PRD-13 follow-up — re-send a single already-delivered email.
// Mirrors useRetryAssignment's shape so the consumer pattern stays
// uniform (loading state per id via ``mutation.variables``).
export const useResendAssignmentEmail = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialAssignmentsApi.resendEmail,
    onSuccess: () => {
      analytics.track("material_email_resent", { scope: "one" })
      invalidateMaterialCaches(qc)
      qc.invalidateQueries({ queryKey: studentMaterialsKeys.all })
    },
  })
}

// PRD-13 §FR-5.1 — re-fire a single FAILED / BOUNCED email send.
// Distinct from useResendAssignmentEmail (which targets `sent`): this
// is the recovery path after the prior send errored. Needed so the
// per-student card has a button to retry a resend that errored —
// otherwise the only affordance after a "Reenviar" failure is the
// material-wide "Reintentar fallidos" header CTA, which is heavier.
export const useRetryAssignmentEmail = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: materialAssignmentsApi.retryEmail,
    onSuccess: () => {
      analytics.track("material_email_resent", { scope: "retry_failed" })
      invalidateMaterialCaches(qc)
      qc.invalidateQueries({ queryKey: studentMaterialsKeys.all })
    },
  })
}

// ── PRD-73 — «Dar más tiempo» ───────────────────────────────────────

/**
 * What a reopen changes, and nothing wider. The material's own branch
 * (`detail(id)` holds its rows, progress and cut) and the grading branch,
 * where the review queue lives on purpose (`grading-review/queryKeys.ts`): a
 * cleared grade sends the live portal version back to review. Only when a
 * grade was actually cleared do the surfaces that show grades refresh too.
 */
const invalidateReopenCaches = (
  qc: ReturnType<typeof useQueryClient>,
  materialId: string,
  gradesCleared: boolean,
) => {
  void qc.invalidateQueries({ queryKey: materialKeys.detail(materialId) })
  void qc.invalidateQueries({ queryKey: gradingKeys.all })
  if (!gradesCleared) return
  void qc.invalidateQueries({ queryKey: materialKeys.lists() })
  void qc.invalidateQueries({ queryKey: progressKeys.all })
  void qc.invalidateQueries({ queryKey: studentKeys.all })
  void qc.invalidateQueries({ queryKey: classroomKeys.all })
  void qc.invalidateQueries({ queryKey: homeKeys.all })
}

/**
 * Reopen ONE student's submission after the deadline (PRD-73 §FR-3). The
 * caller owns the dialog, so the toasts live there; this owns the cache and
 * the analytics, which must fire for every caller.
 */
export const useReopenAssignment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assignment: Pick<MaterialAssignment, "id" | "material_id">) =>
      materialAssignmentsApi.reopen(assignment.id),
    onSuccess: (result: ReopenOut, assignment) => {
      const materialId = assignment.material_id
      // The row flips to «Reabierta» with the dialog's success view, not a
      // refetch later; the invalidation below then settles the rest.
      qc.setQueryData<MaterialAssignment[]>(
        materialKeys.assignmentsForMaterial(materialId),
        (rows) => rows?.map((row) => (row.id === result.assignment.id ? result.assignment : row)),
      )
      if (!result.already_open) {
        analytics.track("material_submission_reopened", {
          material_id: materialId,
          scope: "single",
          count: 1,
          grades_cleared: result.grade_cleared ? 1 : 0,
          without_email: result.email_outcome === "no_email" ? 1 : 0,
        })
      }
      invalidateReopenCaches(qc, materialId, result.grade_cleared)
    },
    // A 409 means the row changed under the professor (scanned, archived):
    // re-read it so the action she just lost disappears.
    onError: (_err, assignment) => {
      void qc.invalidateQueries({
        queryKey: materialKeys.assignmentsForMaterial(assignment.material_id),
      })
    },
  })
}

/** Reopen several students of one material at once (PRD-73 §FR-4). */
export const useBulkReopen = (materialId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assignmentIds: string[]) => materialsApi.reopen(materialId, assignmentIds),
    onSuccess: (result: BulkReopenOut) => {
      if (result.reopened > 0) {
        analytics.track("material_submission_reopened", {
          material_id: materialId,
          scope: "bulk",
          count: result.reopened,
          grades_cleared: result.grades_cleared,
          without_email: result.without_email,
        })
      }
      invalidateReopenCaches(qc, materialId, result.grades_cleared > 0)
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
    },
  })
}

// PRD-16 — bulk download. The mutation returns ``{blob, filename}``;
// the caller is responsible for the browser save side-effect (we keep
// it here instead of in the hook so consumers can distinguish "fetch
// failed" from "browser refused to download" cleanly).
export const useBulkDownloadMaterial = () =>
  useMutation({
    mutationFn: (materialId: string) => materialsApi.downloadBulk(materialId),
    onSuccess: (_result, materialId) =>
      analytics.track("material_downloaded", { material_id: materialId, kind: "bulk_zip" }),
  })

/**
 * PRD-61 §FR-5.1 — which evaluation cut a sent, gradeable material counts
 * for. `enabled` is the caller's gate: the endpoint answers 409
 * `material_not_gradeable` for practice and unsent materials, so asking is
 * pointless there.
 */
export const useMaterialCut = (materialId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: materialKeys.cut(materialId ?? ""),
    queryFn: () => materialsApi.getCut(materialId as string),
    enabled: enabled && !!materialId,
    // No `placeholderData`: across materials it would show A's cut on B, and
    // a change made in that window would PATCH B.
  })

export const useUpdateMaterialCut = (materialId: string, classroomId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cut: number | null) => materialsApi.setCut(materialId, cut),
    onSuccess: (updated, cut) => {
      analytics.track("material_cut_changed", {
        material_id: materialId,
        classroom_id: classroomId,
        cut,
      })
      // The response IS the new state; no refetch needed for this control.
      qc.setQueryData<MaterialCut>(materialKeys.cut(materialId), updated)
      // The roster's per-cut averages moved; so may Home's closing-cut notice.
      void qc.invalidateQueries({ queryKey: classroomKeys.results(classroomId) })
      void qc.invalidateQueries({ queryKey: homeKeys.all })
    },
  })
}

// ── PRD-65 — editing one student's content before it is sent ────────

/** The release's content, the saved draft and the edit state. `enabled`
 *  is the drawer's gate: only fetched once the professor enters edit mode. */
export const useAssignmentContent = (assignmentId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: materialKeys.assignmentContent(assignmentId ?? "none"),
    queryFn: () => materialAssignmentsApi.getContent(assignmentId as string),
    enabled: enabled && !!assignmentId,
    // No `placeholderData`: across students it would show A's text on B.
  })

/** Everything a draft change touches: the content itself, the row's badge
 *  in the list, and the material's dirty / compiling counts. */
const invalidateContentCaches = (
  qc: ReturnType<typeof useQueryClient>,
  assignmentId: string,
  materialId: string,
) => {
  void qc.invalidateQueries({ queryKey: materialKeys.assignmentContent(assignmentId) })
  void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
  void qc.invalidateQueries({ queryKey: materialKeys.detail(materialId) })
}

/**
 * Store (or, with `items: []`, clear) the draft. `hasMath` is the content's
 * flag, carried only so the analytics event can say whether formulas were
 * involved; the text itself never leaves the request. `quiet` is the save
 * inside "Generar PDF": the compile's own outcome is the one confirmation.
 */
export const useSaveContentEdits = (assignmentId: string, materialId: string) => {
  const qc = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation("materials")
  return useMutation({
    mutationFn: ({ items }: { items: ContentEditItem[]; hasMath: boolean; quiet?: boolean }) =>
      materialAssignmentsApi.putContentEdits(assignmentId, items),
    onSuccess: (response, { items, hasMath, quiet }) => {
      if (items.length === 0) {
        analytics.track("material_content_edit_discarded", { material_id: materialId })
        toast.success(t("toast.edits_discarded"))
      } else {
        analytics.track("material_content_edit_saved", {
          material_id: materialId,
          edited_item_count: items.length,
          has_math: hasMath,
        })
        if (!quiet) toast.success(t("toast.edits_saved"))
      }
      // The response IS the new state; write it so the editor's baseline
      // moves before the list refetch lands.
      qc.setQueryData<AssignmentContentResponse>(
        materialKeys.assignmentContent(assignmentId),
        response,
      )
      invalidateContentCaches(qc, assignmentId, materialId)
    },
    onError: (err) => {
      toast.error(t(...apiErrorKeys(err)))
      logger.error("materials.content_edits.save_failed", {
        code: apiErrorCode(err),
        material_id: materialId,
      })
    },
  })
}

/** Render one student's draft into new PDFs; answers the TaskRun to bind to. */
export const useCompileAssignment = (assignmentId: string, materialId: string) => {
  const qc = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation("materials")
  return useMutation({
    mutationFn: () => materialAssignmentsApi.compile(assignmentId),
    onSuccess: () => {
      analytics.track("material_content_compile_requested", {
        material_id: materialId,
        source: "drawer",
      })
      invalidateContentCaches(qc, assignmentId, materialId)
    },
    onError: (err) => {
      toast.error(t(...apiErrorKeys(err)))
      logger.error("materials.content_edits.compile_failed", {
        code: apiErrorCode(err),
        material_id: materialId,
      })
    },
  })
}

/**
 * Render every dirty student's draft, before approving (PRD-65 FR-18). The
 * server dispatches what fits the professor's free in-flight slots and
 * answers `remaining` for the rest. The caller shows failures inline in the
 * send modal, so this hook logs and never toasts (notifications-toast.md §9).
 */
export const useCompileMaterialEdits = (materialId: string) => {
  const qc = useQueryClient()
  return useMutation({
    // `followUp`: a later pass of the same approval (free slots ran out).
    // The professor asked once, so the event fires once.
    mutationFn: (_vars: { followUp: boolean }) => materialsApi.compileEdits(materialId),
    onSuccess: (_res, { followUp }) => {
      if (!followUp) {
        analytics.track("material_content_compile_requested", {
          material_id: materialId,
          source: "approve",
        })
      }
      void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
      void qc.invalidateQueries({ queryKey: materialKeys.detail(materialId) })
    },
    onError: (err) => {
      const code = apiErrorCode(err)
      // Nothing left to compile is the state we wanted: the counts were
      // stale by a realtime event or so.
      if (code === "material_no_dirty_edits") {
        logger.info("materials.content_edits.compile_all_noop", { material_id: materialId })
        void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
        return
      }
      // No free slot yet: the wait asks again once a compile settles.
      if (code === "material_too_many_in_flight") {
        logger.info("materials.content_edits.compile_all_waiting_slot", {
          material_id: materialId,
        })
        return
      }
      logger.error("materials.content_edits.compile_all_failed", {
        code,
        material_id: materialId,
      })
    },
  })
}

/**
 * The compile's TaskRun, and what happens when it ends (celery-progress.md
 * §13): on completion the content, the signed URLs and the row refresh and
 * the caller leaves edit mode; on failure the draft (now carrying the error,
 * which the compile bar shows inline) refreshes and the caller keeps
 * editing. Each run settles once.
 */
export const useAssignmentCompileProgress = ({
  assignmentId,
  materialId,
  taskRunId,
  rowCompiling,
  onCompleted,
  onFailed,
}: {
  assignmentId: string
  materialId: string
  taskRunId: string | null
  /** The row's own `edit_state === "compiling"`, from the content query. */
  rowCompiling: boolean
  onCompleted: () => void
  onFailed: () => void
}) => {
  const qc = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation("materials")
  const tracked = useTaskProgress(taskRunId)
  // `useTaskProgress` keeps the previous run's payload as placeholder while
  // the next one loads (PRD-21 FR-12). A retry after a failed compile would
  // read the OLD run's `failed` and settle on the spot: only this run counts.
  const progress = tracked && tracked.task_run_id === taskRunId ? tracked : undefined
  const settledRunId = useRef<string | null>(null)
  // Read through refs so a parent re-render never re-arms the effect.
  const callbacks = useRef({ onCompleted, onFailed })
  callbacks.current = { onCompleted, onFailed }

  // The row leaving `compiling` (its ready / failed event refetched it after
  // commit) ends the run even when the last TaskRun read predates that
  // commit: read the run again so the settle below sees the terminal state.
  const wasCompiling = useRef(rowCompiling)
  useEffect(() => {
    const left = wasCompiling.current && !rowCompiling
    wasCompiling.current = rowCompiling
    if (left && taskRunId) void qc.invalidateQueries({ queryKey: taskRunKeys.detail(taskRunId) })
  }, [rowCompiling, taskRunId, qc])

  useEffect(() => {
    if (!progress || !taskRunId || settledRunId.current === taskRunId) return
    if (progress.status === "completed") {
      settledRunId.current = taskRunId
      // The whole assignment branch: content, both signed URLs, starter file.
      void qc.invalidateQueries({ queryKey: materialKeys.assignment(assignmentId) })
      void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
      void qc.invalidateQueries({ queryKey: materialKeys.detail(materialId) })
      toast.success(t("toast.pdf_updated"))
      logger.info("materials.content_edits.compiled", { material_id: materialId })
      callbacks.current.onCompleted()
    } else if (progress.status === "failed") {
      settledRunId.current = taskRunId
      invalidateContentCaches(qc, assignmentId, materialId)
      logger.error("materials.content_edits.compile_run_failed", {
        code: progress.error_code,
        material_id: materialId,
      })
      callbacks.current.onFailed()
    }
  }, [progress, taskRunId, assignmentId, materialId, qc, toast, t])

  return progress
}

type ApproveStage = "idle" | "compiling" | "approving" | "failed"
interface SendDecision {
  sendDate: string | null
  deadline: string | null
}

const isPendingEdit = (a: MaterialAssignment) =>
  a.edit_state === "dirty" || a.edit_state === "compiling"

/**
 * Approve, compiling every edited student first (PRD-65 FR-18).
 *
 * The wait is driven by the assignments query: the `material.assignment_*`
 * events refetch it, and `realtime.connected` plus a last-resort tick cover
 * a dropped event. Every decision is taken on a snapshot NEWER than the last
 * move (a compile request, a 409), so a stale "nobody pending" never
 * approves and a stale "dirty" never asks twice:
 *
 *  - dirty rows left, no request in flight → ask `compile-edits` again; it
 *    dispatches what fits the free slots, and a 429 means "no slot yet";
 *  - nobody dirty or compiling → approve with the decision she made;
 *  - an edited student failed → stop, naming that student.
 *
 * Closing the modal (`reset`) drops the decision and nothing else: the
 * compiles finish on their own and the material stays in `revision`.
 */
export const useApproveWithCompile = ({
  materialId,
  assignments,
  assignmentsUpdatedAt,
  onApproved,
}: {
  materialId: string | undefined
  assignments: MaterialAssignment[]
  assignmentsUpdatedAt: number
  onApproved: (updated: Material) => void
}) => {
  const qc = useQueryClient()
  const { t } = useTranslation("materials")
  const { mutate: approveMutate } = useApproveMaterial()
  const compileEdits = useCompileMaterialEdits(materialId ?? "")
  const { mutate: compileMutate, isPending: requesting } = compileEdits
  const [stage, setStage] = useState<ApproveStage>("idle")
  const [decision, setDecision] = useState<SendDecision | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // The snapshot must be newer than this before the wait decides anything.
  const [notBefore, setNotBefore] = useState(0)
  // Everyone this send waits on, grown from fresh data (a 409 can add a
  // student), so the line reads "n of N" and never "0 of 0".
  const [involved, setInvolved] = useState<string[]>([])
  // A pass could not take everybody (`remaining` > 0, or a 429).
  const [inTurns, setInTurns] = useState(false)
  // Bumped by every run and every reset: a callback from a flow the
  // professor already closed must not reopen it.
  const flow = useRef(0)
  // Whether this flow already asked once (the analytics event fires once).
  const askedInFlow = useRef(false)

  const pending = useMemo(() => assignments.filter(isPendingEdit), [assignments])
  const dirtyCount = useMemo(
    () => assignments.filter((a) => a.edit_state === "dirty").length,
    [assignments],
  )
  const failedRow = useMemo(
    () => assignments.find((a) => a.edit_state === "failed") ?? null,
    [assignments],
  )
  if (stage === "compiling") {
    const missing = pending.filter((a) => !involved.includes(a.id))
    if (missing.length > 0) setInvolved([...involved, ...missing.map((a) => a.id)])
  }
  const done = useMemo(() => {
    const waiting = new Set(pending.map((a) => a.id))
    return involved.filter((id) => !waiting.has(id)).length
  }, [involved, pending])

  const fail = useCallback(
    (message: string, code: string | null, step: "compile" | "approve") => {
      setStage("failed")
      // The modal shows it inline (role=alert); no toast on top of it.
      setErrorMessage(message)
      logger.error(
        step === "approve" ? "materials.approve.failed" : "materials.approve.compile_first_failed",
        { code, material_id: materialId },
      )
    },
    [materialId],
  )

  const requestCompile = useCallback(() => {
    const id = flow.current
    setNotBefore(Date.now())
    const followUp = askedInFlow.current
    askedInFlow.current = true
    compileMutate(
      { followUp },
      {
        onSuccess: (res) => {
          if (flow.current === id && res.remaining > 0) setInTurns(true)
        },
        onError: (err) => {
          if (flow.current !== id) return
          const code = apiErrorCode(err)
          if (code === "material_too_many_in_flight") {
            setInTurns(true)
            return
          }
          if (code === "material_no_dirty_edits") return
          fail(t(...apiErrorKeys(err)), code, "compile")
        },
      },
    )
  }, [compileMutate, fail, t])

  const runApprove = useCallback(
    (d: SendDecision) => {
      if (!materialId) return
      const id = flow.current
      setStage("approving")
      approveMutate(
        { id: materialId, sendDate: d.sendDate, studentUploadDeadline: d.deadline },
        {
          // Even if the modal closed meanwhile: the material WAS approved.
          onSuccess: (updated) => {
            setStage("idle")
            setDecision(null)
            onApproved(updated)
          },
          onError: (err) => {
            const code = apiErrorCode(err)
            if (flow.current !== id) {
              logger.error("materials.approve.failed", { code, material_id: materialId })
              return
            }
            if (code === "material_edits_uncompiled") {
              // Someone edited meanwhile (another tab): wait again, on fresh data.
              setNotBefore(Date.now())
              setStage("compiling")
              void qc.invalidateQueries({
                queryKey: materialKeys.assignmentsForMaterial(materialId),
              })
              return
            }
            fail(t(...apiErrorKeys(err)), code, "approve")
          },
        },
      )
    },
    [approveMutate, fail, materialId, onApproved, qc, t],
  )

  const failOnRow = useCallback(
    (row: MaterialAssignment) =>
      fail(
        t("send_modal.compile_failed", { name: row.student_name }),
        row.edit_error_code,
        "compile",
      ),
    [fail, t],
  )

  const run = useCallback(
    (sendDate: string | null, deadline: string | null) => {
      flow.current += 1
      askedInFlow.current = false
      const d = { sendDate, deadline }
      setDecision(d)
      setErrorMessage(null)
      setInTurns(false)
      setInvolved(pending.map((a) => a.id))
      if (failedRow) {
        failOnRow(failedRow)
        return
      }
      if (pending.length === 0) {
        runApprove(d)
        return
      }
      // The snapshot on screen is enough to start: the wait below asks for
      // the compiles straight away.
      setNotBefore(0)
      setStage("compiling")
    },
    [failOnRow, failedRow, pending, runApprove],
  )

  // The wait itself: every refetch of the assignments re-runs this.
  useEffect(() => {
    if (stage !== "compiling" || !decision) return
    if (failedRow) {
      failOnRow(failedRow)
      return
    }
    if (assignmentsUpdatedAt <= notBefore) return
    if (pending.length === 0) {
      runApprove(decision)
      return
    }
    if (dirtyCount > 0 && !requesting) requestCompile()
  }, [
    stage,
    decision,
    failedRow,
    assignmentsUpdatedAt,
    notBefore,
    pending.length,
    dirtyCount,
    requesting,
    runApprove,
    requestCompile,
    failOnRow,
  ])

  // A dropped `material.assignment_*` event must not hang the send: re-read
  // the rows on (re)connect, and — only while waiting — at the feature's
  // last-resort poll cadence. The events stay the signal; this is the net.
  useRealtimeEvent("realtime.connected", () => {
    if (stage === "compiling" && materialId) {
      void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
    }
  })
  useEffect(() => {
    if (stage !== "compiling" || !materialId) return
    const tick = window.setInterval(() => {
      void qc.invalidateQueries({ queryKey: materialKeys.assignmentsForMaterial(materialId) })
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(tick)
  }, [stage, materialId, qc])

  const reset = useCallback(() => {
    flow.current += 1
    setStage("idle")
    setDecision(null)
    setErrorMessage(null)
    setInTurns(false)
  }, [])

  return {
    run,
    reset,
    stage,
    done,
    total: involved.length,
    inTurns,
    errorMessage,
    /** An edited student whose last compile failed: the send cannot go yet. */
    failedStudentName: failedRow?.student_name ?? null,
    isPending: stage === "compiling" || stage === "approving",
    /** Cancel stays available while waiting; only the approval itself is final. */
    canCancel: stage !== "approving",
  }
}
